import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Dialog, Field, Input, toast } from "@dl/web-core-js/ui";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, refusalMessage } from "../../api/manufacturing";
import { isoDate } from "../../api/reporting";
import { ServiceOrderClient, type ServiceOrderDTO, type ServiceOrderLineDTO, type ServiceShipmentResult } from "../../api/servicedesk";

/**
 * Parts leaving for the customer. Each line opens at what is still to send,
 * because that is what usually goes in the box; clear a line that is not
 * ready. Shipping everything closes the order.
 */
export function ShipDialog({
  order,
  lines,
  onClose,
  onPosted,
}: {
  order: ServiceOrderDTO;
  lines: ServiceOrderLineDTO[];
  onClose: () => void;
  onPosted: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const orders = useMemo(() => new ServiceOrderClient(http), [http]);
  const [quantities, setQuantities] = useState<Record<number, string>>(() =>
    Object.fromEntries(lines.map((line) => [line.id, Number(line.quantity_outstanding ?? 0) > 0 ? String(line.quantity_outstanding) : ""])),
  );
  const [tracking, setTracking] = useState("");
  const [date, setDate] = useState(() => isoDate(new Date()));
  const [result, setResult] = useState<ServiceShipmentResult | null>(null);

  const chosen = lines
    .map((line) => ({ line, quantity: Number(quantities[line.id]) }))
    .filter(({ quantity }) => Number.isFinite(quantity) && quantity > 0);
  // A negative typed in the box would be dropped silently; it blocks the shipment instead.
  const invalid = (line: ServiceOrderLineDTO) => {
    const typed = quantities[line.id]?.trim() ?? "";
    return typed !== "" && Number(typed) < 0;
  };
  const anyInvalid = lines.some(invalid);

  const post = useMutation({
    mutationFn: () =>
      orders.ship(order.id, {
        document_date: date || null,
        reference: tracking.trim() || null,
        note: null,
        lines: chosen.map(({ line, quantity }) => ({ service_order_line_id: line.id, quantity })),
      }),
    onSuccess: async (posted) => {
      setResult(posted);
      await onPosted();
      toast.success(
        posted.closed
          ? t("sd.shipped-closed", "Shipment #{{id}} posted; the order is complete", { id: posted.stock_document_id })
          : t("sd.shipped", "Shipment #{{id}} posted", { id: posted.stock_document_id }),
      );
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("sd.ship.title", "Ship service order {{n}}", { n: order.number })}
      actions={
        result ? (
          <Button variant="primary" onClick={onClose}>
            {t("button.close", "Close")}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t("dialog.cancel", "Cancel")}
            </Button>
            <Button variant="primary" disabled={chosen.length === 0 || anyInvalid} loading={post.isPending} onClick={() => post.mutate()}>
              {chosen.length === 1 ? t("sd.ship.post-one", "Ship 1 line") : t("sd.ship.post", "Ship {{n}} lines", { n: chosen.length })}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <table className="w-full text-[13px] tabular-nums">
          <thead className="text-left text-[11px] uppercase tracking-wide text-ink-faint">
            <tr>
              <th className="py-1">{t("column.part.number", "Number")}</th>
              <th className="py-1 text-right">{t("column.sd.shipped", "Shipped")}</th>
              <th className="py-1 text-right">{t("sd.ship.left", "Still to send")}</th>
            </tr>
          </thead>
          <tbody>
            {result.lines.map((line) => (
              <tr key={line.stock_movement_id} className="border-t border-line">
                <td className="py-1">
                  {line.part_number}
                  {line.over_shipped && (
                    <Badge variant="warning" size="sm" className="ml-2">
                      {t("sd.ship.over", "more than asked")}
                    </Badge>
                  )}
                </td>
                <td className="py-1 text-right">{formatNumber(line.quantity)}</td>
                <td className="py-1 text-right">{formatNumber(line.outstanding_after)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("sd.ship.tracking", "Tracking number")} htmlFor="sd-ship-tracking">
              <Input id="sd-ship-tracking" value={tracking} onChange={(event) => setTracking(event.target.value)} />
            </Field>
            <Field label={t("form.field.date", "Date")} htmlFor="sd-ship-date">
              <Input id="sd-ship-date" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
            </Field>
          </div>
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full text-[13px]">
              <thead className="bg-surface-sunk text-left text-[11px] uppercase tracking-wide text-ink-faint">
                <tr>
                  <th className="px-3 py-2">{t("column.part.number", "Number")}</th>
                  <th className="px-3 py-2 text-right">{t("column.bom.quantity", "Qty")}</th>
                  <th className="px-3 py-2 text-right">{t("sd.ship.left", "Still to send")}</th>
                  <th className="px-3 py-2 text-right">{t("sd.ship.now", "In the box")}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={line.id} className="border-t border-line">
                    <td className="px-3 py-1.5">
                      <span className="font-medium">{line.part_number}</span>
                      <span className="ml-2 text-[11px] text-ink-faint">{line.part_name}</span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatNumber(line.quantity)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatNumber(line.quantity_outstanding)}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Input
                        type="number"
                        step="any"
                        min="0"
                        value={quantities[line.id] ?? ""}
                        invalid={invalid(line)}
                        aria-label={t("sd.ship.now", "In the box")}
                        onChange={(event) => setQuantities((current) => ({ ...current, [line.id]: event.target.value }))}
                        className="ml-auto h-8 w-24 text-right"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {anyInvalid && <p className="text-[12px] text-risk">{t("sd.ship.invalid", "A quantity in the box cannot be negative.")}</p>}
        </div>
      )}
    </Dialog>
  );
}
